/** @jsxImportSource @emotion/react */
import styled from '@emotion/styled';
import { FormContent } from 'components';
import { Formik, Form, FormikHelpers } from 'formik';
import { FormErrors } from 'hooks';
import { GlobalErrors } from 'models';
import React, { useMemo } from 'react';
import {
  FormPage,
  FormPageAuthInputData,
  Submissions,
  SubmissionButton,
  GlobalErrorMessages,
  SubmissionErrorMessages,
  FormPageAuthProperty,
  FormPageAuthProperties,
} from 'shared';
import { CurrentFormPage } from 'store';
import { validator } from 'validators';
import { convertFormPropertiesToArray } from '../shared';
import { Properties } from './Properties';
import { convertInitialValues } from './utils';

export type OnSubmissionClickPayload = {
  endpoint: SubmissionButton['endpoint'];
  pageId: FormPage['pageId'];
  values: FormPageAuthInputData;
  formik: FormikHelpers<FormPageAuthInputData>;
};

export type AuthContentProps = {
  formPage: NonNullable<CurrentFormPage>;
  submitting: boolean;
  onSubmissionClick: (payload: OnSubmissionClickPayload) => Promise<void>;
  updateGlobalError: (
    errorMessage: FormPage['invalidInputError']['authenticationFailMessage'],
  ) => void;
  errors: FormErrors;
  globalErrors?: GlobalErrors;
};

const doNothing = () => {
  return;
};

const GlobalErrorContent = styled.div`
  outline: none;
`;

export const AuthContent = React.forwardRef<HTMLDivElement, AuthContentProps>(
  (
    {
      formPage,
      errors,
      submitting,
      onSubmissionClick,
      updateGlobalError,
      globalErrors = [],
    },
    ref,
  ) => {
    const {
      design,
      properties: formPageProperties,
      submissions,
      invalidInputError,
      pageId,
    } = formPage;
    const { authenticationFailMessage } = invalidInputError;
    const properties = useMemo(
      () => convertFormPropertiesToArray<FormPageAuthProperty>(formPage),
      [formPage],
    );
    const initialValues = convertInitialValues({
      properties: formPageProperties as FormPageAuthProperties,
    });
    const buttonDisabled = {
      next: submitting,
      previous: submitting,
    };
    return (
      <FormContent>
        <Formik
          enableReinitialize
          initialValues={initialValues}
          validate={validator(formPage)}
          onSubmit={doNothing}
        >
          {(formik) => {
            const { values } = formik;
            const globalErrorMessages =
              globalErrors.length > 0 ? globalErrors : errors.global;
            const displayGlobalError = globalErrorMessages.length > 0;

            const displaySubmissionError = errors.submission.length > 0;
            return (
              <Form>
                <GlobalErrorContent
                  css={{ marginBottom: displayGlobalError ? '24px' : 0 }}
                  ref={ref}
                  tabIndex={-1}
                >
                  <GlobalErrorMessages
                    displayError={displayGlobalError}
                    errorMessages={globalErrorMessages}
                  />
                </GlobalErrorContent>
                <Properties
                  properties={properties}
                  design={design}
                  disabled={submitting}
                />
                {submissions && (
                  <Submissions
                    submissions={submissions}
                    onNextButtonClick={async (endpoint) => {
                      formik.handleSubmit();
                      const errors = await formik.validateForm();
                      if (Object.keys(errors).length) {
                        updateGlobalError(authenticationFailMessage);
                        return;
                      }
                      onSubmissionClick({ endpoint, pageId, values, formik });
                    }}
                    disabled={buttonDisabled}
                    design={design}
                  />
                )}

                <SubmissionErrorMessages
                  displayError={displaySubmissionError}
                  errorMessages={errors.submission}
                  css={{
                    marginTop: displaySubmissionError ? '8px' : 0,
                  }}
                />
              </Form>
            );
          }}
        </Formik>
      </FormContent>
    );
  },
);
